import { createMachine, transition, type AnyMachineSnapshot } from "xstate";
import { CARE_TIERS, EVENTS, type CareTierId } from "@/data/tunables";
import { CARDS } from "@/data/cards";
const CARDS_BY_ID = new Map(CARDS.map((c) => [c.id, c] as const));
import type { GameState } from "@/state/GameState";

/**
 * M5 — Event channels (v0.13 RFC §3.3): the scheduler's per-channel
 * lifecycle as charts, PROJECTION model like M1–M4 (machines derive
 * state from the plain save; transitions DESCRIBE, the engine applies).
 *
 *   idle ──ROLL[weight hit]──▶ pending ──COLLECT──▶ cooldown ──▶ idle
 *                                   └────EXPIRE────▶ cooldown
 *
 * Satire has no pending (fire-and-forget): idle ⇄ cooldown.
 */

export type LootChannelEvent =
  | { type: "ROLL"; hit: boolean }
  | { type: "COLLECT"; active: boolean }
  | { type: "EXPIRE" }
  | { type: "COOL"; lapsed: boolean };

export const lootChannelMachine = createMachine({
  types: {} as { events: LootChannelEvent },
  id: "lootChannel",
  initial: "idle",
  states: {
    idle: {
      on: { ROLL: { target: "pending", guard: ({ event }) => event.type === "ROLL" && event.hit, actions: [{ type: "spawnLoot" }] } },
    },
    pending: {
      on: {
        COLLECT: { target: "cooldown", guard: ({ event }) => event.type === "COLLECT" && event.active, actions: [{ type: "payout" }] },
        EXPIRE: { target: "cooldown", actions: [{ type: "despawn" }] },
      },
    },
    cooldown: {
      on: { COOL: { target: "idle", guard: ({ event }) => event.type === "COOL" && event.lapsed } },
    },
  },
});

export const satireChannelMachine = createMachine({
  id: "satireChannel",
  initial: "idle",
  states: {
    idle: { on: { ROLL: { target: "cooldown", actions: [{ type: "toast" }] } } },
    cooldown: { on: { COOL: "idle" } },
  },
});

/** Where a tier's loot channel stands, derived from the save. */
export function projectLootChannel(state: GameState, tier: CareTierId): AnyMachineSnapshot {
  const ev = state.run.events;
  let value: string;
  if (ev.active.some((l) => l.tier === tier)) value = "pending";
  else if ((ev.cooldownLeft[`loot:${tier}`] ?? 0) > 0) value = "cooldown";
  else value = "idle";
  return lootChannelMachine.resolveState({ value }) as AnyMachineSnapshot;
}

export function projectSatireChannel(state: GameState): AnyMachineSnapshot {
  const value = (state.run.events.cooldownLeft.satire ?? 0) > 0 ? "cooldown" : "idle";
  return satireChannelMachine.resolveState({ value }) as AnyMachineSnapshot;
}

export type LootCollectFire =
  | { allowed: true; kind: "money" | "contraband"; tier: CareTierId; item: string; value: number }
  | { allowed: false; reason: "unknownId" };

/**
 * The collect arbiter (routes actions.collectLoot). Pure — the caller
 * applies funding/heat/SK and removes the record. Refusing an id that
 * is not active is the machine's COLLECT guard doing its job.
 */
export function fireLootCollect(state: GameState, lootId: number): LootCollectFire {
  const loot = state.run.events.active.find((l) => l.id === lootId);
  const before = loot ? projectLootChannel(state, loot.tier) : lootChannelMachine.resolveState({ value: "idle" });
  const [after, actions] = transition(lootChannelMachine, before as never, { type: "COLLECT", active: loot !== undefined });
  const paid = actions.some((a) => (a as { type?: string }).type === "payout");
  if (loot === undefined || !paid || after.value !== "cooldown") {
    return { allowed: false, reason: "unknownId" };
  }
  return { allowed: true, kind: loot.kind, tier: loot.tier, item: loot.item, value: loot.value };
}

/** Channel roster for the Control Room / diagram generator. */
export function allEventChannels(): Array<{ id: string; tier?: CareTierId }> {
  return [{ id: "events:satire" }, ...CARE_TIERS.map((t) => ({ id: `events:loot:${t}`, tier: t }))];
}

export const cardMachine = createMachine({
  id: "eventCard",
  initial: "idle",
  states: {
    idle: { on: { ROLL: "open" } },
    open: { on: { ANSWER: "cooldown", EXPIRE: "cooldown" } },
    cooldown: { on: { COOL: "idle" } },
  },
});

/** Where a scene's card channel stands, derived from the save. */
export function projectCardChannel(state: GameState, scene: string): AnyMachineSnapshot {
  const ev = state.run.events;
  const value = ev.cards.some((c) => c.scene === scene)
    ? "open"
    : (ev.cooldownLeft[`card:${scene}`] ?? 0) > 0
      ? "cooldown"
      : "idle";
  return cardMachine.resolveState({ value }) as AnyMachineSnapshot;
}

export type CardAnswerFire =
  | { allowed: true; defId: string }
  | { allowed: false; reason: "unknownId" | "badChoice" };

/** Answer arbiter (routes actions.answerCard). Pure — the caller applies
 * the chosen branch's effects + clears the card. */
export function fireCardAnswer(state: GameState, cardId: number, choiceIndex: number): CardAnswerFire {
  const card = state.run.events.cards.find((c) => c.id === cardId);
  if (card === undefined) return { allowed: false, reason: "unknownId" };
  const choices = CARDS_BY_ID.get(card.defId)?.choices.length ?? 0;
  if (choiceIndex < 0 || choiceIndex >= choices) return { allowed: false, reason: "badChoice" };
  return { allowed: true, defId: card.defId };
}

/** The contraband economics (RULED §8.3b) — one place, tunables-fed. */
export function contrabandOutcome(state: GameState, value: number): { funding: number; heat: number; sk: number } {
  const c = EVENTS.contraband;
  if (state.run.criminal.unlocked) {
    return { funding: Math.round(value * c.fenceMult), heat: c.heatPerGrab, sk: c.skTrickle };
  }
  return { funding: Math.round(value * c.dudMult), heat: c.heatPerGrab, sk: 0 };
}
